import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { Checklist, Measurement } from '@cognite/apm-client';
import type { ExternalDatapoint } from '@cognite/sdk';

import { MutationKeys, QueryKeys } from '../../../../utils/queryKeys';
import { METRICS_NAMESPACES } from '../../../metrics/constants';

import { MeasurementNumericReadingUpsertContext } from './use-measurement-numeric-reading-upsert.context';

type MutationContext = {
  cachedIsOnline: boolean;
};

export const useMeasurementNumericReadingUpsert = () => {
  const {
    useFDMServices,
    useCurrentUserContext,
    useNetworkStatusContext,
    useMetrics,
    makeToast,
    captureException,
  } = useContext(MeasurementNumericReadingUpsertContext);

  const { measurementsService, timeseriesService } = useFDMServices();
  const queryClient = useQueryClient();
  const { isOnline } = useNetworkStatusContext();
  const { t } = useTranslation('measurement');
  const { user } = useCurrentUserContext();
  const metrics = useMetrics(METRICS_NAMESPACES.auditMeasurement);

  const { mutateAsync } = useMutation<
    { measurement: Measurement; onMeasurementChange?: () => void },
    Error,
    {
      measurement: Measurement;
      datapointExternalId: string;
      previousMeasureTimestamp?: number;
      newDatapoint: ExternalDatapoint | null;
      onMeasurementChange?: () => void;
    },
    MutationContext
  >({
    mutationFn: async ({
      measurement,
      datapointExternalId,
      previousMeasureTimestamp,
      newDatapoint,
      onMeasurementChange,
    }) => {
      // if previous datapoint was made, delete it
      if (previousMeasureTimestamp) {
        await timeseriesService.deleteDataPoint(
          datapointExternalId,
          previousMeasureTimestamp
        );
      }

      try {
        if (newDatapoint) {
          await timeseriesService.insertDataPoint(
            datapointExternalId,
            newDatapoint
          );
        }

        await measurementsService.updateMeasurements([measurement], user!);
        return { measurement, onMeasurementChange };
      } catch (error) {
        throw new Error(String(error));
      }
    },
    onMutate: async ({
      onMeasurementChange,
      measurement: updatedMeasurement,
    }) => {
      if (!isOnline) {
        if (onMeasurementChange) {
          onMeasurementChange();
        }

        await queryClient.cancelQueries({ queryKey: [QueryKeys.CHECKLIST] });

        queryClient.setQueriesData<Checklist>(
          { queryKey: [QueryKeys.CHECKLIST] },
          (old) => {
            if (!old) return old;
            return {
              ...old,
              checklistItems: old.checklistItems?.map((item) => {
                const measurements = item.measurements ?? [];
                let didPatch = false;

                const nextMeasurements = measurements.map((measurement) => {
                  if (
                    measurement.externalId !== updatedMeasurement.externalId
                  ) {
                    return measurement;
                  }
                  didPatch = true;
                  return { ...measurement, ...updatedMeasurement };
                });

                return didPatch
                  ? { ...item, measurements: nextMeasurements }
                  : item;
              }),
            };
          }
        );
      }

      return {
        cachedIsOnline: isOnline,
      };
    },
    onError: (err) => {
      makeToast({
        body: t('READING_REQUEST_UPDATE_ERROR', 'Failed to update reading'),
        type: 'danger',
      });
      captureException(err, {
        level: 'error',
        tags: {
          mutationKey: MutationKeys.USE_MEASURMENT_NUMERIC_READING_UPSERT,
        },
      });
    },
    onSuccess: async ({ onMeasurementChange }, _, context) => {
      metrics.track('Update');

      // cachedIsOnline is used to check if the user was online when the mutation was triggered
      if (context?.cachedIsOnline) {
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.TIMESERIES],
        });
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.CHECKLIST],
        });

        if (onMeasurementChange) {
          onMeasurementChange();
        }
      }
    },
    mutationKey: [MutationKeys.USE_MEASURMENT_NUMERIC_READING_UPSERT],
    retry: 10,
    retryDelay: 2000,
  });

  return {
    mutateAsync,
  };
};
